import React, { useEffect, useState, useRef } from "react";
import { api, extractError } from "@/lib/api";
import { Link } from "react-router-dom";
import { formatDateIt } from "@/lib/format";
import { Plus, X, Trash2, Pencil, Trophy, Building2, User, Users as UsersIcon, Search } from "lucide-react";
import ClientAutocomplete from "@/components/ClientAutocomplete";

// A "successo" (win) is like a note but can be linked to multiple clients,
// multiple collaborators, and/or scoped to "studio" (the firm as a whole).
export default function Successi() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [q, setQ] = useState("");
    const [scope, setScope] = useState("");
    const [show, setShow] = useState(null);   // null | "new" | success object (edit)

    const load = async () => {
        const params = {};
        if (q) params.q = q;
        if (scope) params.scope = scope;
        const { data } = await api.get("/successes", { params });
        setItems(data);
    };
    useEffect(() => {
        (async () => {
            const [c, co] = await Promise.all([
                api.get("/clients"), api.get("/collaborators"),
            ]);
            setClients(c.data); setCollaborators(co.data);
        })();
    }, []);
    useEffect(() => { load(); }, [q, scope]); // eslint-disable-line

    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const collabMap = Object.fromEntries(collaborators.map((c) => [c.id, c]));

    const remove = async (id) => {
        if (!window.confirm("Eliminare questo successo?")) return;
        await api.delete(`/successes/${id}`); load();
    };

    return (
        <div className="p-6" data-testid="successi-page">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Successi</h1>
                    <div className="text-sm text-neutral-500">Storie di vittoria dello studio · {items.length} totali</div>
                </div>
                <button onClick={() => setShow("new")} data-testid="new-success-btn"
                    className="inline-flex items-center gap-1.5 bg-neutral-900 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-neutral-800">
                    <Plus size={14} /> Nuovo successo
                </button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-3 mb-4 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel testo…"
                        data-testid="successi-search"
                        className="w-full pl-7 pr-3 py-1.5 text-sm border border-neutral-200 rounded" />
                </div>
                <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="successi-scope"
                    className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti</option>
                    <option value="client">Con clienti</option>
                    <option value="collaborator">Con collaboratori</option>
                    <option value="studio">Studio</option>
                </select>
            </div>

            {items.length === 0 ? (
                <div className="bg-white border border-dashed border-neutral-200 rounded-md p-10 text-center" data-testid="successi-empty">
                    <Trophy className="mx-auto text-amber-500" size={28} />
                    <div className="font-display text-neutral-800 font-medium mt-2">Nessun successo</div>
                    <div className="text-sm text-neutral-500 mt-1">Racconta la prima storia di successo dello studio.</div>
                </div>
            ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {items.map((s) => (
                        <li key={s.id} className="bg-white border border-neutral-200 rounded-md p-4" data-testid={`success-${s.id}`}>
                            <div className="flex items-start gap-2">
                                <div className="p-2 rounded bg-amber-50 border border-amber-100 text-amber-600"><Trophy size={16} /></div>
                                <div className="flex-1 min-w-0">
                                    {s.title && <div className="font-display font-semibold text-neutral-900">{s.title}</div>}
                                    <div className="text-sm text-neutral-800 whitespace-pre-line mt-0.5">{s.content}</div>
                                    <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                                        {s.is_studio && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-neutral-200 bg-neutral-50 text-neutral-700"><Building2 size={10} /> Studio</span>}
                                        {(s.client_ids || []).map((cid) => {
                                            const c = clientMap[cid];
                                            return c && <Link key={cid} to={`/clienti/${cid}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sky-100 bg-sky-50 text-sky-800 hover:bg-sky-100"><User size={10} /> {c.first_name} {c.last_name}</Link>;
                                        })}
                                        {(s.collaborator_ids || []).map((cid) => {
                                            const c = collabMap[cid];
                                            return c && <Link key={cid} to={`/collaboratori/${cid}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-100 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"><UsersIcon size={10} /> {c.name}</Link>;
                                        })}
                                    </div>
                                    <div className="text-xs text-neutral-400 mt-2">
                                        {s.event_date ? formatDateIt(s.event_date) : formatDateIt(s.created_at)}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <button onClick={() => setShow(s)} data-testid={`edit-success-${s.id}`} className="text-neutral-400 hover:text-neutral-700"><Pencil size={13} /></button>
                                    <button onClick={() => remove(s.id)} data-testid={`delete-success-${s.id}`} className="text-neutral-400 hover:text-red-600"><Trash2 size={13} /></button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {show && (
                <SuccessEditor
                    clients={clients} collaborators={collaborators}
                    initial={show === "new" ? null : show}
                    onClose={() => setShow(null)}
                    onSaved={() => { setShow(null); load(); }}
                />
            )}
        </div>
    );
}

function SuccessEditor({ clients, collaborators, initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial ? {
        title: initial.title || "",
        content: initial.content || "",
        event_date: initial.event_date || "",
        client_id: (initial.client_ids || [])[0] || "",
        collaborator_id: (initial.collaborator_ids || [])[0] || "",
        is_studio: !!initial.is_studio,
    } : {
        title: "", content: "", event_date: new Date().toISOString().slice(0, 10),
        client_id: "", collaborator_id: "", is_studio: false,
    });
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

    // Enforce mutual exclusion: studio XOR (client|collab)
    const setStudio = (v) => setForm({ ...form, is_studio: v, client_id: v ? "" : form.client_id, collaborator_id: v ? "" : form.collaborator_id });
    const setClient = (id) => setForm({ ...form, client_id: id, is_studio: id ? false : form.is_studio });
    const setCollab = (id) => setForm({ ...form, collaborator_id: id, is_studio: id ? false : form.is_studio });

    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            const payload = {
                title: form.title, content: form.content,
                event_date: form.event_date || null,
                client_ids: form.client_id ? [form.client_id] : [],
                collaborator_ids: form.collaborator_id ? [form.collaborator_id] : [],
                is_studio: form.is_studio,
            };
            if (initial?.id) await api.patch(`/successes/${initial.id}`, payload);
            else await api.post("/successes", payload);
            onSaved();
        } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                data-testid="success-editor"
                className="bg-white rounded-md shadow-lg w-full max-w-xl max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">{initial ? "Modifica successo" : "Nuovo successo"}</div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    <label className="block">
                        <span className="text-xs text-neutral-600">Titolo (facoltativo)</span>
                        <input data-testid="success-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded" />
                    </label>
                    <label className="block">
                        <span className="text-xs text-neutral-600">Storia*</span>
                        <textarea required data-testid="success-content" rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                            className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded" />
                    </label>
                    <label className="block">
                        <span className="text-xs text-neutral-600">Data</span>
                        <input type="date" data-testid="success-date" value={form.event_date || ""} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                            className="mt-1 px-2 py-1.5 border border-neutral-300 rounded" />
                    </label>
                    <div className="p-3 rounded border border-neutral-200 bg-neutral-50/50">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.is_studio} onChange={(e) => setStudio(e.target.checked)} data-testid="success-studio" />
                            <Building2 size={13} /> <span>Successo dello studio (esclusivo)</span>
                        </label>
                        <div className={`mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 ${form.is_studio ? "opacity-40 pointer-events-none" : ""}`}>
                            <div>
                                <div className="text-xs text-neutral-600 mb-1">Cliente</div>
                                <ClientAutocomplete clients={clients} value={form.client_id} onChange={setClient} testId="success-client-autocomplete" allowCreate={false} />
                            </div>
                            <div>
                                <div className="text-xs text-neutral-600 mb-1">Collaboratore</div>
                                <CollaboratorAutocomplete items={collaborators} value={form.collaborator_id} onChange={setCollab} testId="success-collab-autocomplete" />
                            </div>
                        </div>
                        <div className="text-xs text-neutral-500 mt-2">Puoi selezionare cliente e collaboratore insieme, ma non lo studio con nessun altro.</div>
                    </div>
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50 sticky bottom-0">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy} data-testid="save-success" className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? "Salvataggio…" : "Salva"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function CollaboratorAutocomplete({ items, value, onChange, testId }) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    useEffect(() => {
        if (value) {
            const c = items.find((x) => x.id === value);
            if (c) setQ(c.name || "");
        } else setQ("");
    }, [value, items]);
    useEffect(() => {
        const h = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);
    const trimmed = q.trim().toLowerCase();
    const filtered = items.filter((c) => !trimmed || (c.name || "").toLowerCase().includes(trimmed)).slice(0, 20);
    return (
        <div ref={rootRef} className="relative" data-testid={testId}>
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); if (value) onChange(""); }}
                onFocus={() => setOpen(true)} placeholder="Cerca collaboratore…"
                data-testid={`${testId}-input`}
                className="w-full pl-7 pr-7 py-1.5 border border-neutral-300 rounded text-sm" />
            {value && (
                <button type="button" onClick={() => { setQ(""); onChange(""); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"><X size={12} /></button>
            )}
            {open && (
                <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filtered.length === 0 && <div className="p-3 text-xs text-neutral-500">Nessun collaboratore</div>}
                    {filtered.map((c) => (
                        <button key={c.id} type="button" onClick={() => { setQ(c.name || ""); setOpen(false); onChange(c.id); }}
                            data-testid={`${testId}-option-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-neutral-50 border-b border-neutral-50 last:border-b-0 text-sm">
                            {c.name || "—"}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
